import { EssayFiltersViewProps } from "@/components/shared/EssayFiltersView";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/utils/httpClient";
import { sampleToSampleCard } from "@/utils/helpers";
import { useRouter } from "next/navigation";
import { isEqual } from "lodash";
import { FormFieldParams } from "@/components/forms/filterFormFields";
import { SampleCardProps } from "@/components/shared/SampleCard";
import { useRedirect } from "@/utils/useRedirect";

const ACCEPTABLE_PARAMS = [
  "idPaperType",
  "idAcademicLevel",
  "idPaperFormat",
  "idSubcategory",
  "idEssayType",
  "idTag",
  "pagesFrom",
  "pagesTo",
  "wordsFrom",
  "wordsTo",
  "title",
  "volume",
] as const;

export type FilterParam = typeof ACCEPTABLE_PARAMS[number];

export type FilterParamValue = number | string | (number | string)[] | undefined;

export type SelectedValues = Partial<Record<FilterParam, FilterParamValue>>;

export type SectionFiltersProps = EssayFiltersViewProps & {
  total: number;
  addParams: SelectedValues;
  initialParams: SelectedValues;
  filterParams?: FormFieldParams;
};

export type UsePageFiltersReturnType = {
  filterProps: { baseUrl: string; filterParams?: FormFieldParams | undefined };
  currentItems: SampleCardProps[];
  count: number;
  page: number;
  currentTotal: number;
  selectedValues: SelectedValues;
  onValueChange: (newValues: SelectedValues) => void;
};

export const usePageFilters = (props: SectionFiltersProps): UsePageFiltersReturnType => {
  const { items, currentPage, pagesCount, total, addParams, initialParams, ...filterProps } = props;
  const [currentItems, setCurrentItems] = useState(items);
  const [count, setCount] = useState(pagesCount);
  const [page, setPage] = useState(currentPage);
  const [currentTotal, setCurrentTotal] = useState(total);
  const redirect = useRedirect();

  const [selectedValues, setSelectedValues] = useState<SelectedValues>(initialParams);

  const handleChange = async (selectedValues: SelectedValues) => {
    const params = {
      page: 1,
      ...selectedValues,
    };

    const paramsString = Object.entries(params)
      .map(([key, value]) => {
        if (key === "page") return;

        if (Array.isArray(value)) {
          return value
            .reduce((prev: string, next: string | number) => (prev || "") + `${key}=${next}&`, "")
            .slice(0, -1);
        } else if (value) {
          return `${key}=${value}`;
        }

        return false;
      })
      .filter(Boolean)
      .join("&");

    const newUrl = window.location.pathname.replace(/\/\d$/g, "") + (paramsString ? `?${paramsString}` : "");

    redirect(newUrl);
  };

  useEffect(() => {
    setCurrentTotal(total);
  }, [total]);

  const onValueChange = (newValues: SelectedValues) => {
    const sanitizedNewValues: SelectedValues = Object.entries(newValues)
      .filter((value) => (Array.isArray(value) ? value.length : !!value))
      .reduce(
        (prev, [key, value]) => ({
          ...prev,
          [key]: value,
        }),
        {}
      );

    if (!("title" in newValues) && selectedValues.title) {
      sanitizedNewValues.title = selectedValues.title;
    }

    const valuesChanged = !isEqual(sanitizedNewValues, selectedValues);

    if (valuesChanged) {
      setSelectedValues(sanitizedNewValues);
      handleChange(sanitizedNewValues);
    }
  };

  useEffect(() => {
    setCurrentItems(items);
  }, [items]);

  useEffect(() => {
    setCount(pagesCount);
  }, [pagesCount]);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  return {
    filterProps,
    currentItems,
    count,
    page,
    currentTotal,
    selectedValues,
    onValueChange,
  };
};
